import { useEffect, useState, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faClock,
  faExclamationTriangle,
  faBatteryQuarter,
  faSignal,
  faSatelliteDish,
  faTowerBroadcast,
} from '@fortawesome/free-solid-svg-icons'
import { useAccountContext } from '../contexts/AccountContext'
import { DeviceStatusIcon } from '../components/DeviceStatusIcon'
import { useDeviceStatuses } from '../hooks/useDeviceStatuses'
import { useAuth } from '../hooks/useAuth'
import { useMissingReadingsSummary } from '../hooks/admin/useMissingReadings'
import { useFleetSignal } from '../hooks/useSignalStrength'
import { DeviceInfo } from '../common/enums'
import { api } from '../services'
import DeviceDetailPanel from '../components/DeviceDetailPanel'
import MissingReadingsCard from '../components/admin/MissingReadingsCard'
import ActionCard, { ActionTone, AllClearBadge } from '../components/dashboard/ActionCard'
import WeakSignalList from '../components/dashboard/WeakSignalList'
import SignalStrips from '../components/dashboard/SignalStrips'
import { fleetHealth, groupByAccount } from '../utils/fleetHealth'

const RECENT_ALERTS_LIMIT = 10

/** Window the Missing Readings summary tile counts over. */
const MISSING_READINGS_SUMMARY_DAYS = 7

/** Window the Signal summary tile counts over. */
const SIGNAL_SUMMARY_DAYS = 7

/**
 * History the weak-signal panel's daily strips show. Longer than the summary
 * on purpose: the ranking is "worst right now", the strips are whether it has
 * always been that way. Days before capture began draw as "No data".
 */
const SIGNAL_STRIP_DAYS = 30

/**
 * RSSI capture began on this day. Until the summary window is fully covered,
 * "last 7 days" would overstate the history behind the count.
 */
const SIGNAL_CAPTURE_START = Date.UTC(2026, 8, 29)

function signalWindowLabel(now = Date.now()): string {
  const windowStart = now - SIGNAL_SUMMARY_DAYS * 24 * 60 * 60 * 1000
  return windowStart < SIGNAL_CAPTURE_START ? 'since 29 Sep' : `last ${SIGNAL_SUMMARY_DAYS} days`
}

/**
 * Bands that warrant attention on the dashboard. 'fair' is deliberately
 * excluded - it is the band a healthy single-station device lands in, and
 * surfacing every one of those would bury the genuinely marginal installs.
 */
const WEAK_SIGNAL_QUALITIES = new Set(['critical', 'weak'])

/** Which detail panel is revealed below the summary row, if any. */
type PanelKey = 'offline' | 'silent' | 'alerts' | 'battery' | 'missing' | 'signal'
type OpenPanel = PanelKey | null

/**
 * One thing the overview counts. With a count above zero (or still loading) it
 * is a "needs attention" tile that opens its list; at zero it collapses to an
 * all-clear badge. The tile title doubles as the opened panel's heading, so
 * what you clicked and what appeared always read the same.
 */
interface AttentionItem {
  key: PanelKey
  title: string
  /** null while loading. */
  count: number | null
  subtitle?: string
  icon: IconDefinition
  tone: ActionTone
  clearLabel: string
}

const panelId = (key: PanelKey) => `dashboard-panel-${key}`

interface AlertHistoryItem {
  id: number
  deviceId: number
  alertType: string
  message: string
  createdAt: string
  deviceName?: string
}

interface AlertSummary {
  totalAlerts: number
  activeAlerts: number
  recentAlerts: AlertHistoryItem[]
}

interface DeviceAlertState {
  deviceId: number
  sigfoxId: string
  deviceName: string
  isOffline: boolean
  isLowBattery: boolean
  lastBatteryVoltage?: number
  lastReadingTimestamp?: number
}

function formatTimestamp(timestamp: number | string | undefined): string {
  if (!timestamp) return 'N/A'
  const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString()
}

function formatTimeAgo(timestamp: number | string | undefined): string {
  if (!timestamp) return ''
  const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp)
  if (isNaN(date.getTime())) return ''

  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

/** Shell for a revealed detail panel, so they all read the same. */
function DetailPanel({
  id,
  title,
  icon,
  note,
  children,
}: {
  id: string
  title: string
  icon: any
  /** What the list means or what to do about it, under the heading. */
  note?: string
  children: React.ReactNode
}) {
  return (
    <div id={id} className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <FontAwesomeIcon icon={icon} className="text-gray-400" />
        {title}
      </h3>
      {note && <p className="mt-1 text-sm text-gray-500">{note}</p>}
      <div className="mt-3">{children}</div>
    </div>
  )
}

/** Rows for devices with no reading in 30+ days; each opens the device view. */
function SilentDeviceList({ devices, onOpen }: { devices: DeviceInfo[]; onOpen: (sigfoxId: string) => void }) {
  return (
    <ul className="divide-y divide-gray-100 pb-2 pl-6">
      {devices.map((device) => (
        <li key={device.sigfoxId}>
          <button
            type="button"
            onClick={() => onOpen(device.sigfoxId)}
            className="flex w-full items-center gap-3 rounded px-2 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <span className="font-medium text-gray-800">{device.deviceName || device.sigfoxId}</span>
            {device.siteName && <span className="text-gray-500">{device.siteName}</span>}
          </button>
        </li>
      ))}
    </ul>
  )
}

export default function DashboardPage() {
  const { devices, selectedDevice, selectDevice } = useAccountContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAdmin } = useAuth()
  const admin = isAdmin()

  // Honor ?sigfoxId=… so the selection survives reload and is shareable.
  // When the id leaves the URL (the browser's Back from a device opened here),
  // the selection goes with it, so Back lands on the overview again. Only on
  // that transition: a device picked in the sidebar never had it in the URL.
  const urlSigfoxId = searchParams.get('sigfoxId') || ''
  const previousUrlSigfoxId = useRef(urlSigfoxId)
  useEffect(() => {
    if (urlSigfoxId && urlSigfoxId !== selectedDevice) {
      selectDevice(urlSigfoxId)
    } else if (!urlSigfoxId && previousUrlSigfoxId.current && selectedDevice) {
      selectDevice('')
    }
    previousUrlSigfoxId.current = urlSigfoxId
  }, [urlSigfoxId, selectedDevice, selectDevice])

  const focusedDevice: DeviceInfo | undefined = selectedDevice
    ? (devices as Record<string, DeviceInfo>)[selectedDevice]
    : undefined

  // The device view replaces the overview, so start it at the top rather than
  // wherever the overview happened to be scrolled — and put the overview back
  // where it was when the device is closed, so the list that was being worked
  // through is still in view.
  const focusedSigfoxId = focusedDevice?.sigfoxId
  const overviewScrollY = useRef<number | null>(null)
  useEffect(() => {
    if (focusedSigfoxId) {
      window.scrollTo({ top: 0 })
    } else if (overviewScrollY.current !== null) {
      const top = overviewScrollY.current
      overviewScrollY.current = null
      requestAnimationFrame(() => window.scrollTo({ top }))
    }
  }, [focusedSigfoxId])

  // Every device list on this page opens the device the same way. It adds a
  // history entry, so the browser's Back returns to the overview, and keeps
  // the other params (the missing-readings window and account, say) so the
  // overview comes back as it was left.
  const openDevice = (sigfoxId: string) => {
    overviewScrollY.current = window.scrollY
    selectDevice(sigfoxId)
    const next = new URLSearchParams(searchParams)
    next.set('sigfoxId', sigfoxId)
    setSearchParams(next)
  }

  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null)
  const [deviceAlertStates, setDeviceAlertStates] = useState<DeviceAlertState[]>([])
  const [alertLoading, setAlertLoading] = useState(true)
  const [alertError, setAlertError] = useState<string | null>(null)

  // One panel at a time. Devices that stopped reporting recently open by
  // default: they are the ones most likely to be fixable, and they fill the
  // space under the tiles with something worth reading. Clicking the open
  // tile again closes it.
  const [openPanel, setOpenPanel] = useState<OpenPanel>('offline')
  const toggle = (panel: Exclude<OpenPanel, null>) => () =>
    setOpenPanel((current) => (current === panel ? null : panel))

  const devicesArray = useMemo(() => Object.values(devices) as DeviceInfo[], [devices])
  const totalDevices = devicesArray.length

  const { deviceStatuses, isLoading: statusLoading } = useDeviceStatuses(devicesArray)

  // Every device lands in exactly one bucket, so the health bar adds up.
  const health = useMemo(
    () => fleetHealth(devicesArray, deviceStatuses, admin),
    [devicesArray, deviceStatuses, admin]
  )
  const offlineDevices = health.stopped
  const silentGroups = useMemo(() => groupByAccount(health.silent), [health.silent])
  const accountCount = useMemo(
    () => new Set(devicesArray.map((d) => d.accountName).filter(Boolean)).size,
    [devicesArray]
  )

  // Fleet missing-readings totals for the summary tile. Admin-only; the hook
  // is gated so nothing is requested for anyone else.
  const { data: missingSummary } = useMissingReadingsSummary(MISSING_READINGS_SUMMARY_DAYS, admin)

  // Not admin-gated: the endpoint is user-facing and ownership-scoped, so each
  // viewer sees signal for their own devices.
  // With the daily series, for the panel's best/worst strips: one grouped
  // query on the server, so it rides along rather than costing a request.
  const { data: signalSummary, isLoading: signalLoading } = useFleetSignal(
    undefined,
    SIGNAL_SUMMARY_DAYS,
    true,
    SIGNAL_STRIP_DAYS
  )
  const weakSignalDevices = useMemo(
    () => (signalSummary ?? []).filter((row) => WEAK_SIGNAL_QUALITIES.has(row.summary.quality)),
    [signalSummary]
  )

  // Fetch alert data
  useEffect(() => {
    let cancelled = false

    async function fetchAlerts() {
      setAlertLoading(true)
      setAlertError(null)

      try {
        const [summaryResult, statesResult] = await Promise.allSettled([
          api.getAlertHistorySummary(),
          api.getDeviceAlertStates(),
        ])

        if (cancelled) return

        if (summaryResult.status === 'fulfilled') {
          setAlertSummary(summaryResult.value)
        }

        if (statesResult.status === 'fulfilled' && Array.isArray(statesResult.value)) {
          setDeviceAlertStates(statesResult.value)
        }
      } catch (err) {
        if (!cancelled) {
          setAlertError('Failed to load alert data')
        }
      } finally {
        if (!cancelled) {
          setAlertLoading(false)
        }
      }
    }

    fetchAlerts()
    return () => {
      cancelled = true
    }
  }, [])

  const lowBatteryDevices = useMemo(() => {
    return deviceAlertStates.filter((s) => s.isLowBattery)
  }, [deviceAlertStates])

  const activeAlertCount =
    alertSummary?.activeAlerts ?? deviceAlertStates.filter((s) => s.isOffline || s.isLowBattery).length

  const recentAlerts = alertSummary?.recentAlerts ?? []

  // Most urgent first. Devices silent for 30+ days come last: they are worth
  // knowing about, but rarely what someone sets out to fix today.
  const items: AttentionItem[] = [
    {
      key: 'alerts',
      title: 'Active alerts',
      count: alertLoading ? null : activeAlertCount,
      icon: faExclamationTriangle,
      tone: 'red',
      clearLabel: 'No active alerts',
    },
    {
      key: 'offline',
      title: 'Stopped reporting',
      count: statusLoading ? null : offlineDevices.length,
      subtitle: 'devices · last reading 6 h–30 days ago',
      icon: faSignal,
      tone: 'amber',
      clearLabel: 'All reporting',
    },
    {
      key: 'battery',
      title: 'Low battery',
      count: alertLoading ? null : lowBatteryDevices.length,
      subtitle: 'devices',
      icon: faBatteryQuarter,
      tone: 'amber',
      clearLabel: 'Batteries OK',
    },
    ...(admin
      ? [
          {
            key: 'missing' as const,
            title: 'Missing readings',
            count: missingSummary ? missingSummary.totals.devicesAffected : null,
            subtitle: missingSummary
              ? `devices · ${missingSummary.totals.missedMessages.toLocaleString()} missed · ${MISSING_READINGS_SUMMARY_DAYS} days`
              : undefined,
            icon: faSatelliteDish,
            tone: 'red' as const,
            clearLabel: 'Nothing missing',
          },
        ]
      : []),
    {
      key: 'signal',
      title: 'Weak signal',
      count: signalLoading ? null : weakSignalDevices.length,
      subtitle: `devices · ${signalWindowLabel()}`,
      icon: faTowerBroadcast,
      tone: 'amber',
      clearLabel: 'No weak signal',
    },
    {
      key: 'silent',
      title: 'No reading in 30+ days',
      count: statusLoading ? null : health.silent.length,
      subtitle: 'devices',
      icon: faClock,
      tone: 'gray',
      clearLabel: 'None silent 30+ days',
    },
  ]
  const needsAttention = items.filter((item) => item.count !== 0)
  const allClear = items.filter((item) => item.count === 0)
  const itemFor = (key: PanelKey) => items.find((item) => item.key === key)

  // A panel whose count turns out to be zero has nothing to show - its tile
  // has become a badge - so it stays shut. This is what keeps the default
  // 'offline' panel from opening onto an empty list.
  const shownPanel: OpenPanel = openPanel && itemFor(openPanel)?.count === 0 ? null : openPanel
  const panelTitle = (key: PanelKey) => {
    const item = itemFor(key)
    return item ? `${item.title}${item.count ? ` (${item.count.toLocaleString()})` : ''}` : ''
  }

  const reportingNow = health.reporting.length
  const unassignedCount = health.unassigned.length

  return (
    <div className="space-y-6">
      {/* The fleet overview and a selected device are two different views, so
          only one is shown: picking a device replaces this with its dashboard,
          and closing the device brings this back. */}
      {!focusedDevice && (
        <>
          {/* Page Header. The fleet's size is stated once, here; the counts
              below are the parts of it that need someone's attention. */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">
              {admin
                ? `${totalDevices.toLocaleString()} devices across ${accountCount} account${accountCount === 1 ? '' : 's'}`
                : `Your ${totalDevices.toLocaleString()} device${totalDevices === 1 ? '' : 's'}`}
              {admin && unassignedCount > 0 && !statusLoading && ` (${unassignedCount.toLocaleString()} not yet assigned)`}
              {!statusLoading && ` · ${reportingNow.toLocaleString()} reporting in the last 6 h`}
            </p>
          </div>

          {/* Everything clickable sits together under one heading; anything at
              zero drops out of the row and becomes a small all-clear badge, so
              a tile is always something you can open. */}
          <section aria-labelledby="needs-attention-heading" className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 id="needs-attention-heading" className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {needsAttention.length > 0 ? 'Needs attention' : 'All clear'}
              </h2>
              {allClear.length > 0 && (
                <div className="flex flex-wrap gap-2" aria-label="All clear">
                  {allClear.map((item) => (
                    <AllClearBadge key={item.key} label={item.clearLabel} />
                  ))}
                </div>
              )}
            </div>

            {needsAttention.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {needsAttention.map((item) => (
                  <ActionCard
                    key={item.key}
                    title={item.title}
                    value={item.count ?? '…'}
                    subtitle={item.subtitle}
                    icon={item.icon}
                    tone={item.tone}
                    active={shownPanel === item.key}
                    onClick={toggle(item.key)}
                    controls={panelId(item.key)}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-lg bg-white p-4 text-sm text-gray-600 shadow">
                Nothing needs attention right now.
              </p>
            )}
          </section>

          {/* Exactly one revealed panel, directly under the tile that opened it. */}
          {shownPanel === 'offline' && (
            <DetailPanel
              id={panelId('offline')}
              title={panelTitle('offline')}
              icon={faSignal}
              note="Last reading between 6 hours and 30 days ago. These usually recover: check power, antenna and coverage, or contact support."
            >
              {statusLoading ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : offlineDevices.length === 0 ? (
                <p className="text-sm text-gray-400">Every device has reported in the last 6 hours</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {offlineDevices.map((device) => {
                    const status = deviceStatuses[device.sigfoxId]
                    return (
                      <li
                        key={device.sigfoxId}
                        className="py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 rounded"
                        onClick={() => openDevice(device.sigfoxId)}
                      >
                        <div className="flex items-center gap-2">
                          <DeviceStatusIcon status={status?.status ?? 'offline'} />
                          <span className="text-sm font-medium text-gray-800">
                            {device.deviceName || device.sigfoxId}
                          </span>
                          {device.siteName && <span className="text-sm text-gray-500">{device.siteName}</span>}
                        </div>
                        <span className="flex items-center gap-4 text-xs">
                          {admin && device.accountName && <span className="text-gray-500">{device.accountName}</span>}
                          <span className="text-amber-700">{formatTimeAgo(status?.lastReadingTimestamp)}</span>
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </DetailPanel>
          )}

          {shownPanel === 'silent' && (
            <DetailPanel
              id={panelId('silent')}
              title={panelTitle('silent')}
              icon={faClock}
              note="We've received nothing from these devices in over 30 days, so we can't show when they last reported. Contact support to reactivate or retire them."
            >
              {health.silent.length === 0 ? (
                <p className="text-sm text-gray-400">Every device has reported in the last 30 days</p>
              ) : admin ? (
                // Grouped by account, largest first: there is no last-reading
                // time to sort a few hundred devices by.
                <div>
                  {silentGroups.map((group) => (
                    <details key={group.account} className="group border-t border-gray-100 first:border-t-0">
                      <summary className="flex cursor-pointer list-none items-center gap-2 rounded px-2 py-2.5 text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                        <span aria-hidden="true" className="text-gray-400 transition-transform group-open:rotate-90">
                          ›
                        </span>
                        <span className="font-semibold uppercase tracking-wide text-gray-700">{group.account}</span>
                        <span className="ml-auto text-gray-500">
                          {group.devices.length} device{group.devices.length === 1 ? '' : 's'}
                        </span>
                      </summary>
                      <SilentDeviceList devices={group.devices} onOpen={openDevice} />
                    </details>
                  ))}
                </div>
              ) : (
                <SilentDeviceList devices={health.silent} onOpen={openDevice} />
              )}
            </DetailPanel>
          )}

          {shownPanel === 'alerts' && (
            <DetailPanel
              id={panelId('alerts')}
              title={panelTitle('alerts')}
              icon={faExclamationTriangle}
              note={`The ${RECENT_ALERTS_LIMIT} most recent alerts.`}
            >
              {alertLoading ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : alertError ? (
                <p className="text-sm text-gray-400">{alertError}</p>
              ) : recentAlerts.length === 0 ? (
                <p className="text-sm text-gray-400">No recent alerts</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recentAlerts.slice(0, RECENT_ALERTS_LIMIT).map((alert) => (
                    <li key={alert.id} className="py-3 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {alert.deviceName || `Device ${alert.deviceId}`}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{alert.message}</p>
                      </div>
                      <div className="ml-4 flex-shrink-0 text-right">
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                          {alert.alertType}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatTimestamp(alert.createdAt)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </DetailPanel>
          )}

          {shownPanel === 'battery' && (
            <DetailPanel id={panelId('battery')} title={panelTitle('battery')} icon={faBatteryQuarter}>
              {alertLoading ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : lowBatteryDevices.length === 0 ? (
                <p className="text-sm text-gray-400">No low battery alerts</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {lowBatteryDevices.map((device) => (
                    <li
                      key={device.sigfoxId}
                      className="py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 rounded"
                      onClick={() => openDevice(device.sigfoxId)}
                    >
                      <div className="flex items-center gap-2">
                        <DeviceStatusIcon status="online" lowBattery />
                        <span className="text-sm font-medium text-gray-800">
                          {device.deviceName || device.sigfoxId}
                        </span>
                      </div>
                      {device.lastBatteryVoltage !== undefined && (
                        <span className="text-xs text-amber-600 font-medium">
                          {device.lastBatteryVoltage.toFixed(2)}V
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </DetailPanel>
          )}

          {shownPanel === 'signal' && (
            <DetailPanel
              id={panelId('signal')}
              title={panelTitle('signal')}
              icon={faTowerBroadcast}
              note="Worst first. Start with critical devices heard by only one base station."
            >
              {signalLoading ? (
                <p className="text-sm text-gray-500">Loading signal…</p>
              ) : weakSignalDevices.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No devices are reporting weak signal over the last {SIGNAL_SUMMARY_DAYS} days.
                </p>
              ) : (
                <>
                  {/* The picture first, then the detail: the strips show at a
                      glance which installs are bad and whether it is every day,
                      and the table below says why. */}
                  <SignalStrips
                    weakRows={weakSignalDevices}
                    allRows={signalSummary ?? []}
                    days={SIGNAL_STRIP_DAYS}
                    devices={devices as Record<string, DeviceInfo>}
                    showAccount={admin}
                    onOpen={openDevice}
                  />
                  <WeakSignalList
                    rows={weakSignalDevices}
                    devices={devices as Record<string, DeviceInfo>}
                    showAccount={admin}
                    onOpen={openDevice}
                  />
                </>
              )}
            </DetailPanel>
          )}

          {shownPanel === 'missing' && (
            <div id={panelId('missing')}>
              <MissingReadingsCard onOpenDevice={openDevice} />
            </div>
          )}
        </>
      )}

      {/* Selected device detail, shown in place of the fleet overview. */}
      {focusedDevice && (
        <DeviceDetailPanel
          device={focusedDevice}
          status={deviceStatuses[focusedDevice.sigfoxId]?.status || 'pending'}
          lastReadingTimestamp={deviceStatuses[focusedDevice.sigfoxId]?.lastReadingTimestamp}
          isLowBattery={
            deviceAlertStates.find((s) => s.sigfoxId === focusedDevice.sigfoxId)?.isLowBattery ?? false
          }
          activeAlertCount={
            deviceAlertStates.filter(
              (s) => s.sigfoxId === focusedDevice.sigfoxId && (s.isOffline || s.isLowBattery)
            ).length
          }
          onClear={() => {
            selectDevice('')
            // Drop ?sigfoxId from the URL so the URL→state sync effect doesn't
            // immediately re-apply the selection on the next render.
            const next = new URLSearchParams(searchParams)
            next.delete('sigfoxId')
            setSearchParams(next, { replace: true })
          }}
        />
      )}
    </div>
  )
}
